import { useCallback, useMemo, useSyncExternalStore } from 'react';
import useZendeskArticles from './useZendeskArticles';

const STORAGE_KEY = 'whatsNewLastViewedTimestamp';

let listeners: Array<() => void> = [];

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getSnapshot() {
  return localStorage.getItem(STORAGE_KEY);
}

function setLastViewed(value: string) {
  localStorage.setItem(STORAGE_KEY, value);
  listeners.forEach((listener) => listener());
}

function useHasNewArticles() {
  const { data: articles } = useZendeskArticles();
  const lastViewed = useSyncExternalStore(subscribe, getSnapshot);

  const latestCreatedAt = useMemo(() => {
    if (!articles?.length) return null;
    return articles.reduce(
      (latest, article) =>
        article.createdAt > latest ? article.createdAt : latest,
      articles[0].createdAt
    );
  }, [articles]);

  const hasNewArticles = useMemo(() => {
    if (!latestCreatedAt) return false;
    if (!lastViewed) return true;
    return latestCreatedAt > lastViewed;
  }, [latestCreatedAt, lastViewed]);

  const markArticlesViewed = useCallback(() => {
    if (latestCreatedAt) {
      setLastViewed(latestCreatedAt);
    }
  }, [latestCreatedAt]);

  return { hasNewArticles, markArticlesViewed };
}

export default useHasNewArticles;
